import React, { useState, useEffect } from 'react';
import './PrivacyQuiz.css';
import { useProgress } from '../contexts/ProgressContext';

const QUESTIONS = [
  {
    id: 1,
    question: 'What is a zero-knowledge proof?',
    options: [
      { id: 'a', text: 'Showing your password to prove you know it', isCorrect: false },
      { id: 'b', text: 'Proving you know a secret without revealing it', isCorrect: true },
      { id: 'c', text: 'Broadcasting all your data publicly', isCorrect: false },
      { id: 'd', text: 'Deleting all information about a transaction', isCorrect: false }
    ],
    explanation: 'Zero-knowledge means proving a fact without sharing the secret itself. It\'s like proving you know the password by successfully logging in, without ever typing it where others can see.',
    funFact: 'Zero-knowledge proofs were first described in 1985 by MIT researchers!'
  },
  {
    id: 2,
    question: 'Why is privacy important on blockchains?',
    options: [
      { id: 'a', text: 'Just for fun', isCorrect: false },
      { id: 'b', text: 'To keep your money and identity safe', isCorrect: false },
      { id: 'c', text: 'To control what information is public', isCorrect: true },
      { id: 'd', text: 'Blockchains don\'t need privacy', isCorrect: false }
    ],
    explanation: 'Privacy helps you choose what to share. On public blockchains, all transactions are visible forever. Zero-knowledge tech gives you control over your data.',
    funFact: 'Bitcoin addresses can be traced—privacy coins like Zcash use zero-knowledge to hide transaction details!'
  },
  {
    id: 3,
    question: 'What does a "commitment" do in zero-knowledge proofs?',
    options: [
      { id: 'a', text: 'Locks your secret in a cryptographic box', isCorrect: true },
      { id: 'b', text: 'Reveals your private key', isCorrect: false },
      { id: 'c', text: 'Sends your data to everyone', isCorrect: false },
      { id: 'd', text: 'Deletes your information', isCorrect: false }
    ],
    explanation: 'A commitment is like sealing your answer in an envelope. You\'ve committed to a value, but no one can see what it is until you choose to reveal it (if ever).',
    funFact: 'Commitments are used in secure voting, auctions, and even online poker!'
  },
  {
    id: 4,
    question: 'In a zero-knowledge system, what does the "witness" represent?',
    options: [
      { id: 'a', text: 'The person verifying the proof', isCorrect: false },
      { id: 'b', text: 'Your secret that stays hidden', isCorrect: true },
      { id: 'c', text: 'Public blockchain data', isCorrect: false },
      { id: 'd', text: 'A bystander watching the transaction', isCorrect: false }
    ],
    explanation: 'The witness is your private information—the secret you know but never reveal. In ZK, you prove properties about the witness without exposing it.',
    funFact: 'Real zkSNARK witnesses can be thousands of values, all kept private in a single proof!'
  },
  {
    id: 5,
    question: 'Which statement best describes a "verifier" in zero-knowledge?',
    options: [
      { id: 'a', text: 'Someone who generates the proof', isCorrect: false },
      { id: 'b', text: 'Someone who checks if a proof is valid', isCorrect: true },
      { id: 'c', text: 'Someone who steals private data', isCorrect: false },
      { id: 'd', text: 'Someone who deletes proofs', isCorrect: false }
    ],
    explanation: 'The verifier checks that your proof is mathematically valid, without learning your secret. They get convinced you know something, but learn nothing else!',
    funFact: 'ZK verifiers can check proofs in milliseconds, even if the original computation took hours!'
  },
  {
    id: 6,
    question: 'What makes STARK proofs different from other zero-knowledge proofs?',
    options: [
      { id: 'a', text: 'They require a trusted setup ceremony', isCorrect: false },
      { id: 'b', text: 'They\'re transparent and quantum-resistant', isCorrect: true },
      { id: 'c', text: 'They expose all your data', isCorrect: false },
      { id: 'd', text: 'They can only prove simple statements', isCorrect: false }
    ],
    explanation: 'STARKs (Scalable Transparent ARguments of Knowledge) don\'t need trusted setup and are believed to be secure even against quantum computers. Miden VM uses STARKs!',
    funFact: 'STARKs were invented in 2018—they\'re newer than SNARKs but already powering production systems!'
  },
  {
    id: 7,
    question: 'How can you prove your age is over 18 using zero-knowledge?',
    options: [
      { id: 'a', text: 'Show your full birthdate and ID', isCorrect: false },
      { id: 'b', text: 'Tell them your exact age', isCorrect: false },
      { id: 'c', text: 'Generate a proof that age > 18 without revealing exact age', isCorrect: true },
      { id: 'd', text: 'Give them access to all your documents', isCorrect: false }
    ],
    explanation: 'With ZK, you can prove a comparison (age > 18) without revealing your birthdate or exact age. This is called a range proof!',
    funFact: 'ZK age verification could replace showing ID for age-restricted content, protecting your privacy!'
  },
  {
    id: 8,
    question: 'What problem do zkRollups solve?',
    options: [
      { id: 'a', text: 'Making blockchains completely private', isCorrect: false },
      { id: 'b', text: 'Scaling blockchains while keeping security', isCorrect: true },
      { id: 'c', text: 'Removing all transaction fees', isCorrect: false },
      { id: 'd', text: 'Making proofs easier to understand', isCorrect: false }
    ],
    explanation: 'zkRollups process many transactions off-chain, then prove them all at once with a single proof. This makes blockchains faster and cheaper while maintaining security!',
    funFact: 'zkRollups can increase Ethereum\'s throughput by 100x or more!'
  },
  {
    id: 9,
    question: 'In privacy-preserving systems, what should you minimize?',
    options: [
      { id: 'a', text: 'Transaction speed', isCorrect: false },
      { id: 'b', text: 'Information exposure', isCorrect: true },
      { id: 'c', text: 'Proof generation', isCorrect: false },
      { id: 'd', text: 'Network security', isCorrect: false }
    ],
    explanation: 'The golden rule of privacy: share the minimum information needed. Every extra detail you reveal can be used to track or identify you.',
    funFact: 'Data minimization is a core principle of GDPR and modern privacy regulations!'
  },
  {
    id: 10,
    question: 'What does "soundness" mean in zero-knowledge proofs?',
    options: [
      { id: 'a', text: 'The proof makes noise when verified', isCorrect: false },
      { id: 'b', text: 'You cannot fake a proof for a false statement', isCorrect: true },
      { id: 'c', text: 'The proof is easy to understand', isCorrect: false },
      { id: 'd', text: 'The proof is very large', isCorrect: false }
    ],
    explanation: 'Soundness guarantees security: it\'s computationally impossible to create a valid proof if your statement is false. You can\'t cheat the system!',
    funFact: 'Modern ZK systems have 128-bit security—breaking them would take longer than the age of the universe!'
  }
];

const PrivacyQuiz = () => {
  const { updateGameProgress, getProgress } = useProgress();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [quizComplete, setQuizComplete] = useState(false);

  const question = QUESTIONS[currentQuestion];

  useEffect(() => {
    const saved = getProgress('games', 'privacy-quiz');
    if (!saved) {
      updateGameProgress('privacy-quiz', null, 1, false);
    }
  }, [getProgress, updateGameProgress]);

  const handleAnswerSelect = (option) => {
    if (showExplanation) return;
    
    setSelectedAnswer(option);
    setShowExplanation(true);
    
    if (option.isCorrect) {
      setScore(score + 1);
    }
    
    setAnsweredQuestions([...answeredQuestions, {
      question: question.question,
      correct: option.isCorrect,
      selectedAnswer: option.text
    }]);
  };

  const nextQuestion = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizComplete(true);
      const finalScore = Math.round((score + (selectedAnswer?.isCorrect ? 1 : 0)) / QUESTIONS.length * 100);
      updateGameProgress('privacy-quiz', finalScore, QUESTIONS.length, true, 'Privacy Basics');
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setAnsweredQuestions([]);
    setQuizComplete(false);
  };

  const getScoreLevel = (percentage) => {
    if (percentage >= 90) return { label: 'Privacy Pioneer', color: '#10b981', desc: 'Excellent!' };
    if (percentage >= 70) return { label: 'Privacy Pro', color: '#60a5fa', desc: 'Great job!' };
    if (percentage >= 50) return { label: 'Privacy Learner', color: '#f59e0b', desc: 'Good start!' };
    return { label: 'Privacy Novice', color: '#94a3b8', desc: 'Keep learning!' };
  };

  return (
    <div className="privacy-quiz">
      {showIntro && (
        <div className="pq-modal-overlay">
          <div className="pq-modal">
            <div className="pq-modal-header">
              <h2>Privacy Knowledge</h2>
              <button className="pq-close" onClick={() => setShowIntro(false)}>×</button>
            </div>
            <div className="pq-modal-body">
              <p>
                This quiz explores the basics of zero-knowledge and privacy. Select the answer you 
                think is correct—and learn with every try!
              </p>
              <div className="pq-intro-features">
                <div className="pq-feature">
                  <h4>{QUESTIONS.length} Questions</h4>
                  <p>Test your understanding of ZK proofs, privacy, and blockchain technology.</p>
                </div>
                <div className="pq-feature">
                  <h4>Instant Feedback</h4>
                  <p>Learn immediately with detailed explanations and fun facts for every question.</p>
                </div>
                <div className="pq-feature">
                  <h4>No Pressure</h4>
                  <p>Take your time, learn at your own pace, and retake as many times as you want!</p>
                </div>
              </div>
            </div>
            <div className="pq-modal-footer">
              <button className="pq-primary" onClick={() => setShowIntro(false)}>Start Quiz</button>
            </div>
          </div>
        </div>
      )}

      {!quizComplete ? (
        <>
          <div className="pq-header">
            <h1>Privacy Pioneer Quiz</h1>
            <div className="pq-progress-bar">
              <div className="pq-progress-label">
                Question {currentQuestion + 1} of {QUESTIONS.length}
              </div>
              <div className="pq-progress-track">
                <div 
                  className="pq-progress-fill" 
                  style={{ width: `${((currentQuestion + 1) / QUESTIONS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="pq-question-card">
            <div className="pq-question-header">
              <span className="pq-question-number">Question {currentQuestion + 1}</span>
              <span className="pq-current-score">Score: {score}/{currentQuestion + (showExplanation ? 1 : 0)}</span>
            </div>
            
            <h2 className="pq-question-text">{question.question}</h2>

            <div className="pq-options">
              {question.options.map((option) => (
                <div
                  key={option.id}
                  className={`pq-option ${selectedAnswer?.id === option.id ? 'selected' : ''} ${
                    showExplanation && option.isCorrect ? 'correct' : ''
                  } ${showExplanation && selectedAnswer?.id === option.id && !option.isCorrect ? 'incorrect' : ''} ${
                    showExplanation && selectedAnswer?.id !== option.id ? 'dimmed' : ''
                  }`}
                  onClick={() => handleAnswerSelect(option)}
                >
                  <div className="pq-option-indicator">{option.id.toUpperCase()}</div>
                  <div className="pq-option-text">{option.text}</div>
                  {showExplanation && option.isCorrect && (
                    <div className="pq-option-badge correct-badge">Correct</div>
                  )}
                  {showExplanation && selectedAnswer?.id === option.id && !option.isCorrect && (
                    <div className="pq-option-badge incorrect-badge">Incorrect</div>
                  )}
                </div>
              ))}
            </div>

            {showExplanation && (
              <div className="pq-explanation">
                <div className="pq-explanation-content">
                  <h3>Explanation</h3>
                  <p>{question.explanation}</p>
                </div>
                <div className="pq-fun-fact">
                  <strong>Fun Fact:</strong> {question.funFact}
                </div>
                <button className="pq-next-btn" onClick={nextQuestion}>
                  {currentQuestion < QUESTIONS.length - 1 ? 'Next Question' : 'See Results'}
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="pq-completion">
          <div className="pq-completion-header">
            <h1>Quiz Complete!</h1>
            <div className="pq-final-score">
              <div className="pq-score-circle" style={{ 
                borderColor: getScoreLevel(Math.round((score / QUESTIONS.length) * 100)).color 
              }}>
                <span className="pq-score-value">{score}/{QUESTIONS.length}</span>
                <span className="pq-score-percentage">{Math.round((score / QUESTIONS.length) * 100)}%</span>
              </div>
              <div className="pq-score-title" style={{ 
                color: getScoreLevel(Math.round((score / QUESTIONS.length) * 100)).color 
              }}>
                {getScoreLevel(Math.round((score / QUESTIONS.length) * 100)).label}
              </div>
              <div className="pq-score-desc">
                {getScoreLevel(Math.round((score / QUESTIONS.length) * 100)).desc}
              </div>
            </div>
          </div>

          <div className="pq-completion-content">
            <div className="pq-summary">
              <h3>What You Learned</h3>
              <p>
                You've explored the core ideas of zero-knowledge proofs and privacy! From understanding 
                how proofs work without revealing secrets, to learning about STARKs, commitments, and 
                real-world applications—you're ready to dive deeper into the world of privacy-preserving technology.
              </p>
              <p>
                Remember: in web3 and digital life, privacy is power. Every piece of information you 
                keep private is a piece of control you maintain. Zero-knowledge technology is the key 
                to proving what you need while protecting what matters.
              </p>
            </div>

            <div className="pq-question-review">
              <h3>Your Answers</h3>
              {answeredQuestions.map((item, index) => (
                <div key={index} className={`pq-review-item ${item.correct ? 'correct' : 'incorrect'}`}>
                  <div className="pq-review-icon">
                    {item.correct ? '✓' : '✗'}
                  </div>
                  <div className="pq-review-content">
                    <div className="pq-review-question">Q{index + 1}: {item.question}</div>
                    <div className="pq-review-answer">Your answer: {item.selectedAnswer}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pq-completion-actions">
              <button className="pq-restart-btn" onClick={restartQuiz}>
                Retake Quiz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyQuiz;

